import { Grid } from "@mui/material";
import { useAchievements } from "api/achievements";
import { useAppSelector } from "app/hooks";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import _ from "lodash";
import { useState } from "react";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { VariableGroupsDropdown } from "./VariableGroupsDropdown/VariableGroupsDropdown";
import { useStyles } from "./styles";

export function AchievementDropdown({
  disabled,
  fieldName,
  isAchievementForm,
}: {
  disabled?: boolean;
  fieldName: string;
  isAchievementForm?: boolean;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const { values, setFieldValue } = useFormikContext<any>();
  const { useAchievementList, useAchievementData } = useAchievements();
  const [achievementSearch, setAchievementSearch] = useState("");

  const { data: achievementsList } = useAchievementList({
    filters: achievementSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: achievementSearch,
          },
          {
            query: "active",
            condition: "eq",
            value: true,
          },
        ]
      : [
          {
            query: "active",
            condition: "eq",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });

  const value = _.get(values, fieldName);
  const formData = value.data;
  const { data: achievementData } = useAchievementData(formData);
  const defaultLang = useAppSelector(selectDefaultLanguageData);

  const initialValue = achievementData
    ? Array.isArray(achievementData)
      ? achievementData.map((achievement) => ({
          id: achievement.achievementId,
          label: achievement.translations[defaultLang.code]
            ? achievement.translations[defaultLang.code].name
            : "",
        }))
      : {
          id: achievementData.achievementId,
          label: achievementData.translations[defaultLang.code]
            ? achievementData.translations[defaultLang.code].name
            : "",
        }
    : undefined;

  return (
    <>
      <Grid item xs={6}>
        <SelectInput
          className={classes.input}
          data-test-id="condition-operator"
          width="100%"
          disabled={disabled}
          items={[
            {
              id: "is_not_one_of",
              label: message({ id: "is_not_one_of" }),
            },
            {
              id: "is_one_of",
              label: message({ id: "is_one_of" }),
            },
            ...(isAchievementForm
              ? []
              : [
                  {
                    id: "is_one_of_group_values",
                    label: message({ id: "is_one_of_collections" }),
                  },
                  {
                    id: "is_not_one_of_group_values",
                    label: message({ id: "is_not_one_of_collections" }),
                  },
                ]),
          ]}
          label={<RequiredFieldLabel text={message({ id: "condition" })} />}
          name={`${fieldName}.operator`}
          onChange={() => setFieldValue(`${fieldName}.data`, [])}
        />
      </Grid>
      {["is_not_one_of", "is_one_of"].indexOf(value.operator) >= 0 && (
        <Grid item xs={12}>
          <SelectInput
            multiple
            displaySearch
            data-test-id="condition-value"
            width="100%"
            disabled={disabled}
            items={
              achievementsList?.items?.map(({ name, achievementId }) => ({
                label: name,
                id: achievementId,
              })) || []
            }
            label={message({ id: "achievement" })}
            name={`${fieldName}.data`}
            asyncSearch={(searchedQuery: string) =>
              setAchievementSearch(searchedQuery)
            }
            initialValue={initialValue}
            placeholder={message({ id: "achievement" })}
          />
        </Grid>
      )}
      {["is_one_of_group_values", "is_not_one_of_group_values"].indexOf(
        value.operator,
      ) >= 0 && (
        <Grid item xs={12}>
          <VariableGroupsDropdown
            fieldName={`${fieldName}.data.groupOfValues`}
            data-test-id="condition-value"
            placeholder={message({ id: "chooseFromTheList" })}
            value={value.data?.groupOfValues}
          />
        </Grid>
      )}
    </>
  );
}
